import { TOKENS } from '@orgus/tokens';
import type { TokenName } from '@orgus/tokens';

/**
 * Motion presets for the Motion library, read from the motion tokens so apps never write raw
 * durations (motion.md; brief 2.6). Motion is for one-shot moves the user or a real event causes
 * (FLIP between columns, enter and exit); everything perpetual is CSS. The app root wraps
 * everything in <MotionConfig reducedMotion="user">, so each of these is still under reduced
 * motion.
 *
 *   <motion.div layout layoutId={id} transition={motionPresets.move} {...motionPresets.arrive} />
 */
function seconds(name: TokenName): number {
  const v = String(TOKENS[name].value);
  const n = Number.parseFloat(v);
  return v.endsWith('ms') ? n / 1000 : n;
}

function bezier(name: TokenName): [number, number, number, number] {
  const v = TOKENS[name].value as unknown;
  if (Array.isArray(v) && v.length === 4) return v as [number, number, number, number];
  const m = /cubic-bezier\(([^)]+)\)/.exec(String(v));
  const parts = m ? m[1]!.split(',').map((x) => Number.parseFloat(x)) : [0.16, 1, 0.3, 1];
  return parts as [number, number, number, number];
}

const standard = bezier('easing.standard');
const emphasized = bezier('easing.emphasized');

export const motionPresets = {
  /** M6 stage handoff and FLIP reorders: --duration-slow on the standard curve, no overshoot. */
  move: { type: 'tween', duration: seconds('duration.slow'), ease: standard },
  /** Drawers and larger moves. */
  emphasized: { type: 'tween', duration: seconds('duration.slow'), ease: emphasized },
  /** M7 arrival and M8 exit, as Motion props. */
  arrive: {
    initial: { opacity: 0, y: 4 },
    animate: { opacity: 1, y: 0, transition: { type: 'tween', duration: seconds('duration.base'), ease: standard } },
    exit: { opacity: 0, transition: { type: 'tween', duration: seconds('duration.fast'), ease: standard } },
  },
} as const;
